'use client';

import { useState, useRef } from 'react';
import RequirementsPanel from './RequirementsPanel';
import MatchResultsPanel from './MatchResultsPanel';
import CapabilityStrengthBar from './CapabilityStrengthBar';

export interface ProjectRequirements {
  materials: string[];
  processes: string[];
  certifications: string[];
  quantity: string;
  tolerance: string;
  industry: string;
}

export interface CapabilityMatch {
  id: string;
  title: string;
  category: string;
  icon: string;
  score: number;
  matchedFactors: string[];
  description: string;
  specs: { label: string; value: string }[];
  strengths: string[];
  capacityStatus: 'high' | 'medium' | 'low';
  certifications: string[];
  materials: string[];
  processes: string[];
}

const CAPABILITIES: CapabilityMatch[] = [
  {
    id: 'cnc-precision',
    title: 'CNC Precision Machining',
    category: 'Machining',
    icon: 'CogIcon',
    score: 0,
    matchedFactors: [],
    description: 'Ultra-precision 5-axis CNC machining for complex aerospace components with tolerances down to ±0.0001 inches.',
    specs: [
      { label: 'Tolerance', value: '±0.0001"' },
      { label: 'Max Size', value: '60" × 40" × 30"' },
      { label: 'Axes', value: '5-Axis Simultaneous' },
      { label: 'Surface Finish', value: 'Ra 8–32 µin' },
    ],
    strengths: ['Extreme precision', 'Complex geometries', 'High repeatability', 'Multi-material'],
    capacityStatus: 'high',
    certifications: ['AS9100', 'ITAR', 'NADCAP'],
    materials: ['aluminum', 'titanium', 'stainless steel', 'inconel', 'copper'],
    processes: ['cnc machining', 'milling', 'turning', 'drilling', 'boring', '5-axis'],
  },
  {
    id: 'additive-metal',
    title: 'Metal Additive Manufacturing',
    category: 'Additive',
    icon: 'CubeIcon',
    score: 0,
    matchedFactors: [],
    description: 'Advanced DMLS and EBM processes for complex geometries impossible with traditional manufacturing.',
    specs: [
      { label: 'Technology', value: 'DMLS / EBM' },
      { label: 'Build Volume', value: '250 × 250 × 325 mm' },
      { label: 'Layer Thickness', value: '20–100 µm' },
      { label: 'Density', value: '>99.5%' },
    ],
    strengths: ['Complex internal channels', 'Topology optimization', 'Rapid prototyping', 'Weight reduction'],
    capacityStatus: 'medium',
    certifications: ['AS9100', 'ITAR'],
    materials: ['titanium', 'aluminum', 'inconel', 'stainless steel', 'cobalt chrome'],
    processes: ['3d printing', 'additive manufacturing', 'dmls', 'ebm', 'rapid prototyping', 'metal printing'],
  },
  {
    id: 'sheet-metal',
    title: 'Aerospace Sheet Metal',
    category: 'Fabrication',
    icon: 'RectangleStackIcon',
    score: 0,
    matchedFactors: [],
    description: 'Precision sheet metal fabrication for aircraft structures, enclosures, and brackets.',
    specs: [
      { label: 'Thickness', value: '0.016" – 0.250"' },
      { label: 'Bend Radius', value: 'Down to 1T' },
      { label: 'Max Size', value: '120" × 60"' },
      { label: 'Welding', value: 'TIG, MIG, Spot' },
    ],
    strengths: ['Large format parts', 'Welding expertise', 'High volume', 'Cost effective'],
    capacityStatus: 'high',
    certifications: ['AS9100', 'NADCAP'],
    materials: ['aluminum', 'stainless steel', 'titanium', 'steel'],
    processes: ['sheet metal', 'forming', 'bending', 'welding', 'tig welding', 'mig welding', 'stamping', 'punching'],
  },
  {
    id: 'composites',
    title: 'Advanced Composites',
    category: 'Composites',
    icon: 'Square3Stack3DIcon',
    score: 0,
    matchedFactors: [],
    description: 'Carbon fiber and advanced composite manufacturing for lightweight, high-strength aerospace structures.',
    specs: [
      { label: 'Materials', value: 'CFRP, Kevlar, Fiberglass' },
      { label: 'Process', value: 'Autoclave, OOA, Prepreg' },
      { label: 'Max Size', value: '72" × 48" × 24"' },
      { label: 'Fiber Volume', value: 'Up to 65%' },
    ],
    strengths: ['Extreme weight savings', 'High stiffness', 'Thermal stability', 'Custom layup'],
    capacityStatus: 'medium',
    certifications: ['AS9100', 'NADCAP'],
    materials: ['carbon fiber', 'cfrp', 'kevlar', 'fiberglass', 'composite', 'epoxy'],
    processes: ['composites', 'layup', 'autoclave', 'prepreg', 'filament winding', 'resin transfer'],
  },
  {
    id: 'testing-validation',
    title: 'Testing & Validation',
    category: 'Quality',
    icon: 'BeakerIcon',
    score: 0,
    matchedFactors: [],
    description: 'Comprehensive testing including dimensional inspection, NDT, and environmental qualification.',
    specs: [
      { label: 'CMM Accuracy', value: '±0.0001"' },
      { label: 'NDT Methods', value: 'X-Ray, UT, PT, MT' },
      { label: 'Environmental', value: 'Thermal, Vibration, Shock' },
      { label: 'Material Testing', value: 'Tensile, Hardness' },
    ],
    strengths: ['Full traceability', 'NDT certified', 'Environmental qual', 'First article'],
    capacityStatus: 'high',
    certifications: ['AS9100', 'NADCAP', 'ISO 17025'],
    materials: ['aluminum', 'titanium', 'stainless steel', 'composites', 'inconel'],
    processes: ['inspection', 'ndt', 'testing', 'validation', 'qualification', 'cmm', 'x-ray', 'ultrasonic'],
  },
  {
    id: 'surface-treatment',
    title: 'Surface Treatment & Finishing',
    category: 'Finishing',
    icon: 'SparklesIcon',
    score: 0,
    matchedFactors: [],
    description: 'Aerospace-approved surface treatments including anodizing, chemical film, and specialty coatings.',
    specs: [
      { label: 'Anodizing', value: 'Type II, Type III' },
      { label: 'Chem Film', value: 'MIL-DTL-5541' },
      { label: 'Passivation', value: 'AMS 2700' },
      { label: 'Coating', value: 'Thermal Spray, PVD' },
    ],
    strengths: ['Corrosion protection', 'Wear resistance', 'MIL-spec compliance', 'Thermal coatings'],
    capacityStatus: 'high',
    certifications: ['AS9100', 'NADCAP'],
    materials: ['aluminum', 'titanium', 'stainless steel', 'steel'],
    processes: ['anodizing', 'coating', 'passivation', 'plating', 'surface treatment', 'finishing', 'pvd', 'thermal spray'],
  },
];

function computeScore(cap: CapabilityMatch, req: ProjectRequirements): { score: number; matchedFactors: string[] } {
  const factors: string[] = [];
  let score = 0;
  const normalize = (s: string) => s.toLowerCase().trim();

  req.materials.forEach((m) => {
    if (cap.materials.some((cm) => cm.includes(normalize(m)) || normalize(m).includes(cm))) {
      score += 25;
      factors.push(`Material: ${m}`);
    }
  });

  req.processes.forEach((p) => {
    if (cap.processes.some((cp) => cp.includes(normalize(p)) || normalize(p).includes(cp))) {
      score += 20;
      factors.push(`Process: ${p}`);
    }
  });

  req.certifications.forEach((c) => {
    if (cap.certifications.some((cc) => cc.toLowerCase().includes(normalize(c)) || normalize(c).includes(cc.toLowerCase()))) {
      score += 15;
      factors.push(`Cert: ${c}`);
    }
  });

  if (req.tolerance === 'ultra' && cap.id === 'cnc-precision') { score += 20; factors.push('Ultra-tight tolerance'); }
  if (req.tolerance === 'ultra' && cap.id === 'additive-metal') { score += 10; factors.push('Tight tolerance'); }
  if (req.tolerance === 'standard') { score += 5; }

  if (req.quantity === 'prototype' && cap.id === 'additive-metal') { score += 15; factors.push('Rapid prototyping'); }
  if (req.quantity === 'low' && (cap.id === 'cnc-precision' || cap.id === 'additive-metal')) { score += 10; }
  if (req.quantity === 'high' && (cap.id === 'sheet-metal' || cap.id === 'surface-treatment')) { score += 10; factors.push('High volume capacity'); }

  if (req.industry === 'defense' && cap.certifications.includes('ITAR')) { score += 20; factors.push('ITAR compliant'); }
  if (req.industry === 'space' && (cap.id === 'cnc-precision' || cap.id === 'additive-metal' || cap.id === 'composites')) { score += 15; factors.push('Space heritage'); }
  if (req.industry === 'aviation' && cap.certifications.includes('AS9100')) { score += 10; factors.push('AS9100 certified'); }

  return { score: Math.min(score, 100), matchedFactors: factors };
}

const DataStreamLine = ({ delay, top, opacity }: { delay: number; top: string; opacity: number }) => (
  <div
    className="absolute left-0 h-px pointer-events-none"
    style={{
      top,
      opacity,
      width: '100%',
      background: 'linear-gradient(90deg, transparent 0%, rgba(0,212,255,0.6) 30%, rgba(0,212,255,0.9) 50%, rgba(0,212,255,0.6) 70%, transparent 100%)',
      animation: `dataStream ${2.5 + delay}s linear infinite`,
      animationDelay: `${delay}s`,
    }}
  />
);

const ExploreSolutionsClient = () => {
  const [requirements, setRequirements] = useState<ProjectRequirements>({
    materials: [],
    processes: [],
    certifications: [],
    quantity: '',
    tolerance: '',
    industry: '',
  });
  const [matches, setMatches] = useState<CapabilityMatch[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [particlePositions] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 4,
      duration: Math.random() * 3 + 2,
    }))
  );
  const resultsRef = useRef<HTMLDivElement>(null);

  const totalInputs =
    requirements.materials.length +
    requirements.processes.length +
    requirements.certifications.length +
    (requirements.quantity ? 1 : 0) +
    (requirements.tolerance ? 1 : 0) +
    (requirements.industry ? 1 : 0);

  const handleAnalyze = () => {
    if (totalInputs === 0) return;
    setIsAnalyzing(true);
    setScanProgress(0);

    const interval = setInterval(() => {
      setScanProgress((prev) => {
        if (prev >= 100) { clearInterval(interval); return 100; }
        return prev + 3;
      });
    }, 45);

    setTimeout(() => {
      const scored = CAPABILITIES.map((cap) => {
        const { score, matchedFactors } = computeScore(cap, requirements);
        return { ...cap, score, matchedFactors };
      }).sort((a, b) => b.score - a.score);
      setMatches(scored);
      setHasSearched(true);
      setIsAnalyzing(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }, 2000);
  };

  const handleReset = () => {
    setRequirements({ materials: [], processes: [], certifications: [], quantity: '', tolerance: '', industry: '' });
    setMatches([]);
    setHasSearched(false);
  };

  return (
    <div className="min-h-screen bg-background pt-20 relative overflow-hidden">
      {/* Advanced animated background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Fine grid */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        {/* Larger grid overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,212,255,0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,212,255,0.06) 1px, transparent 1px)
            `,
            backgroundSize: '200px 200px',
          }}
        />
        {/* Radial glow zones */}
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #00D4FF 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #1E7B3C 0%, transparent 70%)' }} />

        {/* Floating particles */}
        {particlePositions.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-accent/40"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animation: `float ${p.duration}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}

        {/* Corner brackets */}
        <div className="absolute top-24 left-8 w-12 h-12 border-l-2 border-t-2 border-accent/20" />
        <div className="absolute top-24 right-8 w-12 h-12 border-r-2 border-t-2 border-accent/20" />
        <div className="absolute bottom-8 left-8 w-12 h-12 border-l-2 border-b-2 border-accent/20" />
        <div className="absolute bottom-8 right-8 w-12 h-12 border-r-2 border-b-2 border-accent/20" />
      </div>

      {/* Global scan line during analysis */}
      {isAnalyzing && (
        <div
          className="fixed left-0 right-0 z-50 pointer-events-none"
          style={{
            top: `${scanProgress}%`,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,212,255,0.4) 20%, rgba(0,212,255,1) 50%, rgba(0,212,255,0.4) 80%, transparent 100%)',
            boxShadow: '0 0 30px rgba(0,212,255,0.8), 0 0 60px rgba(0,212,255,0.4)',
            transition: 'top 0.05s linear',
          }}
        />
      )}

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 lg:px-8 py-10">
        {/* ── HERO HEADER ── */}
        <div className="mb-14 relative">
          {/* Top status bar */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent/30 bg-accent/5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-glow" />
                <span className="font-cta text-[10px] text-accent tracking-[0.2em]">SYSTEM ONLINE</span>
              </div>
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-aerospace-green/30 bg-aerospace-green/5">
                <span className="w-1.5 h-1.5 rounded-full bg-aerospace-green" />
                <span className="font-cta text-[10px] text-aerospace-green tracking-[0.2em]">AS9100 CERTIFIED</span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <div className="font-cta text-[10px] text-technical-gray tracking-widest">
                <span className="text-accent">6</span> CAPABILITIES INDEXED
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="font-cta text-[10px] text-technical-gray tracking-widest">
                REAL-TIME MATCHING
              </div>
            </div>
          </div>

          {/* Main title block */}
          <div className="relative">
            {/* Decorative left line */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-accent/40 to-transparent hidden lg:block" />

            <div className="lg:pl-8">
              <div className="font-cta text-[10px] text-technical-gray tracking-[0.3em] mb-3">
                WINGS BHARATH AEROSPACE — CAPABILITY MATCHING ENGINE v2.0
              </div>
              <h1 className="font-accent text-5xl lg:text-7xl font-bold leading-none mb-5">
                <span className="text-foreground">EXPLORE</span>
                <br />
                <span
                  style={{
                    background: 'linear-gradient(135deg, #00D4FF 0%, #00A8CC 40%, #FF6B35 70%, #FF8C5A 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  SOLUTIONS
                </span>
              </h1>
              <p className="font-body text-technical-gray text-base lg:text-lg max-w-2xl leading-relaxed">
                Input your project parameters — materials, processes, certifications, tolerances. Our matching engine cross-references your requirements against Wings Bharath's full manufacturing stack and delivers precision-ranked capability scores.
              </p>

              {/* Quick stats row */}
              <div className="mt-6 flex flex-wrap gap-6">
                {[
                  { value: '500+', label: 'Projects Delivered' },
                  { value: '99.8%', label: 'Quality Rate' },
                  { value: '±0.0001"', label: 'Peak Tolerance' },
                  { value: '<48h', label: 'Quote Turnaround' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-2">
                    <span className="font-accent text-lg text-accent">{stat.value}</span>
                    <span className="font-cta text-[10px] text-technical-gray tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── STRENGTH INDEX ── */}
        <CapabilityStrengthBar />

        {/* ── MAIN MATCHING LAYOUT ── */}
        <div className="mt-10 grid grid-cols-1 xl:grid-cols-[440px_1fr] gap-6 items-start">
          <RequirementsPanel
            requirements={requirements}
            onChange={setRequirements}
            onAnalyze={handleAnalyze}
            onReset={handleReset}
            isAnalyzing={isAnalyzing}
            totalInputs={totalInputs}
          />
          <div ref={resultsRef}>
            <MatchResultsPanel
              matches={matches}
              hasSearched={hasSearched}
              isAnalyzing={isAnalyzing}
              requirements={requirements}
            />
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes dataStream {
          0% { transform: translateX(-100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default ExploreSolutionsClient;
